import { BoilerDataService } from './boiler-data-service.js';
//import { BoilerActorType } from '../../../enums/boilerActorType';

// Load the rendering pieces we want to use (for both WebGL and WebGPU)
import '@kitware/vtk.js/Rendering/Profiles/Geometry';

import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkCylinderSource from '@kitware/vtk.js/Filters/Sources/CylinderSource';
import vtkCubeSource from '@kitware/vtk.js/Filters/Sources/CubeSource';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';
import vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow';
import vtkRenderWindowInteractor from '@kitware/vtk.js/Rendering/Core/RenderWindowInteractor';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
// @ts-ignore
import vtkInteractorStyleTrackballCamera from '@kitware/vtk.js/Interaction/Style/InteractorStyleTrackballCamera';
// @ts-ignore
import vtkCubeAxesActor from '@kitware/vtk.js/Rendering/Core/CubeAxesActor';
import { BoilerPart, GeoBoiler, PartOrientation, Orientation, Location, Part } from '@usimweb/core/models';
import { RGBColor } from '@kitware/vtk.js/types.js';

//FIX ME export to a enum class
// export enum BoilerActorType {
//   CONNECTOR = 'connections',
//   HEAT_EXCHANGER = 'heatExchangers',
//   CYLINDER = 'cylinder',
//   AXES = 'axes',
//   PATCH = 'patch',
// }

export abstract class Boiler {
  private static geoBoiler: GeoBoiler = {} as GeoBoiler;
  private static boilerDataService: BoilerDataService;

  private static renderWindow: vtkRenderWindow;
  private static renderer: vtkRenderer;

  private static currentColor: RGBColor;

  public static initialize(renderDiv: HTMLElement, boilerDataService: BoilerDataService, tree: any) {
    this.boilerDataService = boilerDataService;
    this.geoBoiler = this.boilerDataService.initBoiler(tree);

    const renderWindow = vtkRenderWindow.newInstance();
    const renderer = vtkRenderer.newInstance({ background: [0.98, 0.98, 0.98] });
    renderWindow.addRenderer(renderer);

    this.renderer = renderer;
    this.renderWindow = renderWindow;

    this.createWireframe();

    // ----------------------------------------------------------------------------
    // Use OpenGL as the backend to view the all this
    // ----------------------------------------------------------------------------

    const openglRenderWindow = vtkOpenGLRenderWindow.newInstance();
    renderWindow.addView(openglRenderWindow);

    // ----------------------------------------------------------------------------
    // Set div to put this into the container
    // ----------------------------------------------------------------------------

    openglRenderWindow.setContainer(renderDiv);

    // ----------------------------------------------------------------------------
    // Capture size of the container and set it to the renderWindow
    // ----------------------------------------------------------------------------

    const { width, height } = renderDiv.getBoundingClientRect();
    openglRenderWindow.setSize(width, height);

    // ----------------------------------------------------------------------------
    // Setup an interactor to handle mouse events
    // ----------------------------------------------------------------------------

    const interactor = vtkRenderWindowInteractor.newInstance();
    interactor.setView(openglRenderWindow);
    interactor.initialize();
    interactor.bindEvents(renderDiv);

    // ----------------------------------------------------------------------------
    // Setup interactor style to use
    // ----------------------------------------------------------------------------

    interactor.setInteractorStyle(vtkInteractorStyleTrackballCamera.newInstance());

    this.renderer.resetCamera();
    this.renderWindow.render();
  }

  public static update(tree: any) {
    this.clear();
    this.boilerDataService.clear();
    this.geoBoiler = this.boilerDataService.initBoiler(tree);
    this.createWireframe();

    this.renderer.resetCamera();
    this.renderWindow.render();
  }

  private static createWireframe() {
    // create heat exchangers
    for (let i = 0; i < this.geoBoiler.heatExchangers.length; i++) {
      const cube = this.createHeatExchanger(this.geoBoiler.heatExchangers[i].startHeight.value, this.geoBoiler.heatExchangers[i].endHeight.value, i);
      this.renderer.addActor(cube.actor);
    }

    // create connections
    for (let i = 0; i < this.geoBoiler.connections.length; i++) {
      // const orientation = i % 2 == 0 ? 1 : -1;

      const connection = this.geoBoiler.connections[i];
      const partColor = connection.type.value == 'isolatedBlankFlange' ? { r: 0.3922, g: 0.7059, b: 1 } : { r: 0, g: 0, b: 1 };

      const partOrientation: PartOrientation = {
        orientation: connection.orientation.value as Orientation,
        location: connection.location.value as Location,
      };

      const inner_cylinder = this.createCylinder(
        connection.innerDiameter.value,
        connection.height.value,
        i,
        1,
        BoilerPart.CONNECTOR,
        partColor,
        false,
        connection.displayAngle.value,
        partOrientation,
        connection.tubeLength.value
      );
      const outer_cylinder = this.createCylinder(
        connection.flangeOuterDiameter.value,
        connection.height.value,
        i,
        1,
        BoilerPart.CONNECTOR,
        partColor,
        false,
        connection.displayAngle.value,
        partOrientation,
        connection.tubeLength.value
      );
      const flange = this.createFlange(
        connection.height.value,
        i,
        connection.tubeLength.value,
        connection.flangeOuterDiameter.value,
        connection.flangeThickness.value,
        connection.type.value,
        partColor,
        connection.displayAngle.value,
        partOrientation
      );
      this.renderer.addActor(flange.actor);
      this.renderer.addActor(inner_cylinder.actor);
      this.renderer.addActor(outer_cylinder.actor);
    }

    // create patches
    for (let i = 0; i < this.geoBoiler.patches.length; i++) {
      const patch = this.geoBoiler.patches[i];

      const partOrientation: PartOrientation = {
        orientation: patch.orientation.value as Orientation,
        location: patch.location.value as Location,
      };

      const layer = patch.layer.length > 0 ? patch.layer[0].thickness.value : 0;
      const height = (patch.heightMin.value + patch.heightMax.value) / 2;

      const cylinder = this.createCylinder(patch.area.value, height, i, 1, BoilerPart.PATCH, { r: 0, g: 1, b: 0 }, false, patch.displayAngle.value, partOrientation, layer);

      const flange = this.createFlange(height, i, layer, patch.area.value, 0.001, 'isolatedBlankFlange', { r: 0, g: 1, b: 0 }, patch.displayAngle.value, partOrientation);
      this.renderer.addActor(flange.actor);
      this.renderer.addActor(cylinder.actor);
    }

    // build tank
    const nCells = this.geoBoiler.nCells > 0 ? this.geoBoiler.nCells : 1e-8;
    const innerCylinderPipe: any[] = [];
    const outerCylinderPipe: any[] = [];

    for (let i = 0; i < nCells; i++) {
      innerCylinderPipe.push(this.createCylinderPipeline());
      outerCylinderPipe.push(this.createCylinderPipeline(this.geoBoiler.thickness));
    }

    this.setCylinderPos(innerCylinderPipe);
    this.setCylinderPos(outerCylinderPipe);

    let cubeAxesActor = vtkCubeAxesActor.newInstance();

    const cubeAxes = cubeAxesActor;
    cubeAxes.setCamera(this.renderer.getActiveCamera());

    cubeAxes.getProperty().setColor(0, 0, 0);

    let bounds = outerCylinderPipe[0].actor.getBounds();
    let yBounds = this.getYBounds(outerCylinderPipe);
    cubeAxes.setDataBounds([bounds[0], bounds[1], yBounds[0], yBounds[1], bounds[4], bounds[5]]);

    this.boilerDataService.setActor(0, cubeAxes, BoilerPart.AXES);

    this.renderer.addActor(cubeAxes);
  }

  private static createCylinder(
    diameter: number,
    height: number,
    id: number,
    orientation: number,
    actorType: BoilerPart = BoilerPart.CONNECTOR,
    color: { r: number; g: number; b: number } = { r: 0, g: 0, b: 1 },
    capping = false,
    angle = 0,
    partOrientation: PartOrientation = { orientation: Orientation.RADIAL },
    connection_thickness: number
  ) {
    const cylinderSource = vtkCylinderSource.newInstance();
    const actor = vtkActor.newInstance();
    const mapper = vtkMapper.newInstance();

    actor.setMapper(mapper);
    mapper.setInputConnection(cylinderSource.getOutputPort());

    actor.getProperty().setOpacity(1);
    actor.getProperty().setColor(color.r, color.g, color.b);
    // actor.getProperty().setColor(0.67, 0.388 , 0.98);

    cylinderSource.setHeight(this.geoBoiler.thickness);
    cylinderSource.setRadius(diameter);
    cylinderSource.setResolution(100);
    cylinderSource.setCapping(capping);
    //cylinderSource.setDirectionFrom([1, 0, 0]);

    if (partOrientation.orientation === Orientation.AXIAL) {
      const y = partOrientation.location === Location.TOP ? this.geoBoiler.height + connection_thickness / 2 : -connection_thickness / 2;

      cylinderSource.setDirectionFrom([0, 1, 0]);
      cylinderSource.setCenter(0, y, 0);
    } else {
      const y = height;
      const radius = (this.geoBoiler.diameter / 2 + this.geoBoiler.thickness / 2) * orientation;

      const angleRadians = angle * (Math.PI / 180);
      const x = radius * Math.cos(angleRadians);
      const z = radius * Math.sin(angleRadians);

      cylinderSource.setDirectionFrom([x, 0, z]);
      cylinderSource.setCenter(x, y, z);
    }

    // const y = height - diameter + this.layerOffset;

    this.boilerDataService.setActor(id, actor, actorType);

    const geoBoilerData = this.boilerDataService.getGeoBoiler();
    this.boilerDataService.setGeoBoiler(geoBoilerData);

    return { cylinderSource, mapper, actor };
  }

  private static createFlange(
    height: number,
    id: number,
    connection_thickness: number,
    diameter: number,
    thickness: number,
    flangeType: string,
    color: { r: number; g: number; b: number } = { r: 0, g: 0, b: 1 },
    angle: number,
    partOrientation: PartOrientation = { orientation: Orientation.RADIAL }
  ) {
    const cylinderSource = vtkCylinderSource.newInstance();
    const actor = vtkActor.newInstance();
    const mapper = vtkMapper.newInstance();

    actor.setMapper(mapper);
    mapper.setInputConnection(cylinderSource.getOutputPort());

    const opacity = flangeType === 'isolatedBlankFlange' ? 1 : 0;
    actor.getProperty().setOpacity(opacity);
    actor.getProperty().setColor(color.r, color.g, color.b);

    cylinderSource.setHeight(thickness);
    cylinderSource.setRadius(diameter);
    cylinderSource.setResolution(100);
    cylinderSource.setCapping(true);

    if (partOrientation.orientation === Orientation.AXIAL) {
      const y = partOrientation.location === Location.TOP ? this.geoBoiler.height + connection_thickness : -connection_thickness;

      cylinderSource.setDirectionFrom([0, 1, 0]);
      cylinderSource.setCenter(0, y, 0);
    } else {
      const y = height;
      const radius = this.geoBoiler.diameter / 2 + this.geoBoiler.thickness / 2 + connection_thickness / 2;

      const angleRadians = angle * (Math.PI / 180);
      const x = radius * Math.cos(angleRadians);
      const z = radius * Math.sin(angleRadians);

      cylinderSource.setDirectionFrom([x, 0, z]);
      cylinderSource.setCenter(x, y, z);
    }

    this.boilerDataService.setActor(id, actor, BoilerPart.FLANGE);

    const geoBoilerData = this.boilerDataService.getGeoBoiler();
    this.boilerDataService.setGeoBoiler(geoBoilerData);

    return { cylinderSource, mapper, actor };
  }

  private static createHeatExchanger(startHeight: number, endHeight: number, id: number) {
    const l = endHeight - startHeight;
    const y1 = l / 2 + startHeight;

    const cubeSource = vtkCubeSource.newInstance();
    const actor = vtkActor.newInstance();
    const mapper = vtkMapper.newInstance();

    actor.setMapper(mapper);
    mapper.setInputConnection(cubeSource.getOutputPort());

    actor.getProperty().setOpacity(1);
    actor.getProperty().setColor(1, 0, 0);

    cubeSource.setXLength(0.05);
    cubeSource.setYLength(l);
    cubeSource.setZLength(0.1);

    cubeSource.setCenter(0, y1, 0);

    this.boilerDataService.setActor(id, actor, BoilerPart.HEAT_EXCHANGER);

    const geoBoilerData = this.boilerDataService.getGeoBoiler();
    this.boilerDataService.setGeoBoiler(geoBoilerData);

    return { cubeSource, mapper, actor };
  }

  private static createCylinderPipeline(thickness = 0) {
    const cylinderSource = vtkCylinderSource.newInstance();
    const actor = vtkActor.newInstance();
    const mapper = vtkMapper.newInstance();

    actor.setMapper(mapper);
    mapper.setInputConnection(cylinderSource.getOutputPort());

    actor.getProperty().setOpacity(0.5);

    // if(thickness === 0) {
    //   actor.getProperty().setColor(1, 0 ,0);
    // }

    cylinderSource.setHeight(this.geoBoiler.height / this.geoBoiler.nCells);
    cylinderSource.setRadius(this.geoBoiler.diameter / 2 + thickness);
    cylinderSource.setResolution(100);
    cylinderSource.setCapping(false);
    cylinderSource.setDirectionFrom([0, 1, 0]);

    return { cylinderSource, mapper, actor };
  }

  private static setCylinderPos(cylinderPipe: any) {
    for (let i = 0; i < cylinderPipe.length; i++) {
      const centerY = (this.geoBoiler.height / this.geoBoiler.nCells) * i;
      const y = centerY + this.geoBoiler.height / this.geoBoiler.nCells / 2;

      cylinderPipe[i].cylinderSource.set({ ['center']: [0, y, 0] });
      this.renderer.addActor(cylinderPipe[i].actor);

      this.boilerDataService.setActor(i, cylinderPipe[i].actor, BoilerPart.CYLINDER);
    }
  }

  private static getYBounds(cylinderPipe: any) {
    let y1 = null;
    let y2 = null;

    for (let i = 0; i < cylinderPipe.length; i++) {
      let bounds = cylinderPipe[i].actor.getBounds();

      let tmp_y1 = bounds[2];
      let tmp_y2 = bounds[3];

      if (y1 === null) {
        y1 = tmp_y1;
      } else {
        if (tmp_y1 < y1) {
          y1 = tmp_y1;
        }
      }

      if (y2 === null) {
        y2 = tmp_y2;
      } else {
        if (tmp_y2 > y2) {
          y2 = tmp_y2;
        }
      }
    }

    return [y1, y2];
  }

  public static addHeatExchanger(id: number, data: any) {
    this.boilerDataService.updateActor(data, BoilerPart.HEAT_EXCHANGER);

    const startHeight = data['startHeight'].value;
    const endHeight = data['endHeight'].value;

    const cube = this.createHeatExchanger(startHeight, endHeight, id);
    this.renderer.addActor(cube.actor);

    this.renderer.resetCamera();
    this.renderWindow.render();
  }

  public static addConnector(id: number, data: any) {
    this.boilerDataService.updateActor(data, BoilerPart.CONNECTOR);

    const diameter = data['flangeOuterDiameter'].value;
    const height = data['height'].value;
    const angle = data['displayAngle'].value;
    const tubeLength = data['tubeLength'].value;

    const partOrientation: PartOrientation = {
      orientation: data['orientation'].value as Orientation,
      location: data['location'].value as Location,
    };

    const cylinder = this.createCylinder(diameter, height, id, 1, BoilerPart.CONNECTOR, { r: 0, g: 0, b: 1 }, false, angle, partOrientation, tubeLength);
    this.renderer.addActor(cylinder.actor);

    this.renderer.resetCamera();
    this.renderWindow.render();
  }

  public static addPatch(id: number, data: any) {
    this.boilerDataService.updateActor(data, BoilerPart.PATCH);

    const area = data['area'];
    const diameter = area / Math.PI;
    const height = data['height'].value;
    const angle = data['displayAngle'].value;
    const thickness = data['thickness'].value;

    const partOrientation: PartOrientation = {
      orientation: data['orientation'].value as Orientation,
      location: data['location'].value as Location,
    };

    const cylinder = this.createCylinder(diameter, height, id, 1, BoilerPart.PATCH, { r: 0, g: 0, b: 1 }, true, angle, partOrientation, thickness);
    this.renderer.addActor(cylinder.actor);

    this.renderer.resetCamera();
    this.renderWindow.render();
  }

  public static highLightActor(index: number, type: BoilerPart) {
    const actors = this.boilerDataService.getActorsAt(index, type);
    const partActors = this.boilerDataService.getPartActors();
    const flangeActors = this.boilerDataService.getActorsAt(index, BoilerPart.FLANGE);

    partActors.forEach((actor) => {
      actor.actor.getProperty().setOpacity(0.1);
    });

    actors.forEach((actor) => {
      this.currentColor = actor.actor.getProperty().getColor();
      actor.actor.getProperty().setColor(1, 0.5765, 0);
      actor.actor.getProperty().setOpacity(1);
    });

    if (flangeActors) {
      flangeActors.forEach((actor) => {
        actor.actor.getProperty().setColor(1, 0.5765, 0);
        actor.actor.getProperty().setOpacity(1);
      });
    }

    this.renderWindow.render();
  }

  public static disableHightLightActor(index: number, type: BoilerPart) {
    const actors = this.boilerDataService.getActorsAt(index, type);
    const partActors = this.boilerDataService.getPartActors();
    const flangeActors = this.boilerDataService.getActorsAt(index, BoilerPart.FLANGE);

    actors.forEach((actor) => {
      actor.actor.getProperty().setColor(this.currentColor);
    });

    if (flangeActors) {
      flangeActors.forEach((actor) => {
        actor.actor.getProperty().setColor(this.currentColor);
      });
    }

    partActors.forEach((actor) => {
      actor.actor.getProperty().setOpacity(1);
    });

    this.renderWindow.render();
  }

  public static removeActor(index: number, type: BoilerPart): void {
    const actors = this.boilerDataService.getActorsAt(index, type);

    actors.forEach((actor) => {
      this.renderer.removeActor(actor.actor);
    });

    this.renderer.resetCamera();
    this.renderWindow.render();

    this.boilerDataService.removeActor(type, index);
  }

  public static updateView() {
    this.geoBoiler = this.boilerDataService.getGeoBoiler();

    this.clear();

    setTimeout(() => {
      this.createWireframe();
      this.renderer.resetCamera();
      this.renderWindow.render();
    });
  }

  public static clear() {
    const boilerActors = this.boilerDataService.getActors();

    boilerActors.forEach((boilerActor) => {
      this.renderer.removeActor(boilerActor.actor);
    });

    this.boilerDataService.cleanActors();
  }
}
